import { useId, useRef, useState } from 'react'
import { Check, Copy } from 'lucide-react'
import { validateWavePercent } from './model'
import { aggregateDcRows, dcUnits, dcDelta, type DcRow } from './dc-model'
import type { DerivedDC } from '@/api/distributionCenters'
import { Button } from '@/components/ui/button'

export function DcPercentInput({
  value,
  label,
  onChange,
}: {
  value: number
  label: string
  onChange: (value: number) => void
}) {
  const [editing, setEditing] = useState<string | null>(null)
  const id = useId()
  const error = editing !== null ? validateWavePercent(editing) : null
  return (
    <div className="dc-percent-field">
      <input
        aria-label={label}
        inputMode="decimal"
        value={editing ?? value}
        aria-invalid={!!error}
        aria-describedby={error ? id : undefined}
        onFocus={event => {
          setEditing(String(value))
          event.target.select()
        }}
        onChange={event => {
          const next = event.target.value
          setEditing(next)
          if (!validateWavePercent(next)) onChange(Number(next.trim()))
        }}
        onBlur={() => setEditing(null)}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === 'Escape')
            event.currentTarget.blur()
        }}
      />
      <span aria-hidden="true">%</span>
      {error && (
        <div id={id} role="alert" className="dc-input-error">
          {error} Invalid value is not applied.
        </div>
      )}
    </div>
  )
}

function DcPercentCell({
  value,
  label,
  disabled = false,
  onChange,
}: {
  value: number | null
  label: string
  disabled?: boolean
  onChange: (value: number) => void
}) {
  const [editing, setEditing] = useState<string | null>(null)
  const display = useRef<HTMLButtonElement>(null)
  const finished = useRef(false)
  const id = useId()
  const error =
    editing === null
      ? null
      : editing.trim()
        ? validateWavePercent(editing)
        : 'Enter 0–100 with up to 2 decimal places.'
  const start = () => {
    finished.current = false
    setEditing(value === null ? '' : String(value))
  }
  const finish = (cancel = false, restoreFocus = false) => {
    if (finished.current) return
    finished.current = true
    if (!cancel && editing !== null && !error) onChange(Number(editing.trim()))
    setEditing(null)
    if (restoreFocus) requestAnimationFrame(() => display.current?.focus())
  }
  return (
    <div className="dc-percent-field">
      {editing === null ? (
        <button
          ref={display}
          type="button"
          className="dc-percent-value"
          aria-label={label}
          aria-keyshortcuts="Enter F2"
          title="Double-click to edit, or press Enter or F2"
          disabled={disabled}
          onDoubleClick={start}
          onKeyDown={event => {
            if (event.key === 'Enter' || event.key === 'F2') {
              event.preventDefault()
              start()
            }
          }}
        >
          {disabled ? '—' : value === null ? 'Mixed' : `${dcUnits(value)}%`}
        </button>
      ) : (
        <>
          <input
            ref={element => {
              if (element && document.activeElement !== element) {
                element.focus()
                element.select()
              }
            }}
            aria-label={label}
            inputMode="decimal"
            value={editing}
            aria-invalid={!!error}
            aria-describedby={error ? id : undefined}
            onChange={event => setEditing(event.target.value)}
            onBlur={() => finish()}
            onKeyDown={event => {
              if (event.key === 'Escape') {
                event.preventDefault()
                finish(true, true)
              } else if (event.key === 'Enter') {
                event.preventDefault()
                if (!error) finish(false, true)
              }
            }}
          />
          <span aria-hidden="true">%</span>
          {error && (
            <div id={id} role="alert" className="dc-input-error">
              {error} Invalid value is not applied.
            </div>
          )}
        </>
      )}
    </div>
  )
}

export function DcAllocationGrid({
  rows,
  totalRows,
  waveRows,
  centers,
  waveLabel,
  onEdit,
  onEditAll,
  onCopy,
}: {
  rows: DcRow[]
  totalRows: DcRow[]
  waveRows: DcRow[]
  centers: DerivedDC[]
  waveLabel: string
  onEdit: (row: DcRow, dcId: string, value: number) => void
  onEditAll: (dcId: string, value: number) => void
  onCopy: (row: DcRow) => void
}) {
  const totals = aggregateDcRows(totalRows, centers)
  return (
    <div
      className="dc-grid-scroll"
      tabIndex={0}
      role="region"
      aria-label="Distribution center allocation table, scroll for more destinations"
    >
      <table className="dc-allocation-table">
        <caption className="sr-only">
          {waveLabel}: double-click to edit allocation percentages for each SKU
          and distribution center. Each destination shows theoretical then
          adjusted units. Parent rows are excluded from unit totals.
        </caption>
        <thead>
          <tr>
            <th scope="col" className="dc-sku-column">
              SKU
            </th>
            <th scope="col" className="dc-wave-column">
              Wave units<span>Plan → MOQ</span>
            </th>
            {centers.map(dc => (
              <th scope="col" key={dc.id} title={dc.name}>
                {dc.code}
                <span>Allocation %</span>
              </th>
            ))}
            <th scope="col" className="dc-total-column">
              Allocation check<span>Split / adjusted units</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr className="dc-all-skus-row" data-testid="dc-all-skus">
            <th scope="row" className="dc-sku-column">
              All SKUs
              <span className="mt-1 block text-[10px] font-normal text-muted-foreground">
                {waveRows.length} lines · entire selected wave
              </span>
            </th>
            <td className="dc-wave-column">—</td>
            {centers.map(dc => {
              const first = waveRows[0]?.allocations[dc.id].percent ?? null
              const common = waveRows.every(
                row => row.allocations[dc.id].percent === first
              )
              return (
                <td key={dc.id}>
                  <DcPercentCell
                    value={common ? first : null}
                    label={`All SKUs ${dc.code} allocation percent`}
                    disabled={!waveRows.length}
                    onChange={value => onEditAll(dc.id, value)}
                  />
                </td>
              )
            })}
            <td className="dc-total-column text-[10px] text-muted-foreground">
              Applies across all filters and pages
            </td>
          </tr>
          {rows.map(row => (
            <tr key={row.id} data-testid={`dc-row-${row.id}`}>
              <th scope="row" className="dc-sku-column">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold">{row.line.sku}</span>
                  <span className="dc-type">{row.line.type}</span>
                </div>
                <p
                  className="mt-1 truncate text-[11px] font-normal"
                  title={row.line.description}
                >
                  {row.line.description}
                </p>
                <div className="mt-1 flex items-center justify-between gap-1">
                  <span className="text-[11px] font-normal text-muted-foreground">
                    Case pack: {dcUnits(row.logistics.multiple)} · MOQ{' '}
                    {dcUnits(row.logistics.moq)}
                  </span>
                  <Button
                    variant="ghost"
                    className="h-6 w-6 p-0"
                    aria-label={`Copy ${row.line.sku} ${row.line.type} allocation to other waves`}
                    title="Copy this split to other waves for this SKU line"
                    onClick={() => onCopy(row)}
                  >
                    <Copy size={12} />
                  </Button>
                </div>
                {row.sourceNeedsReview && (
                  <p className="mt-1 text-[10px] font-normal text-amber-800">
                    Review allocation in SKU detail
                  </p>
                )}
                {row.line.type === 'Parent' && (
                  <p className="mt-1 text-[10px] font-normal text-muted-foreground">
                    Reference · excluded from unit totals
                  </p>
                )}
              </th>
              <td className="dc-wave-column">
                <strong>{dcUnits(row.waveQty)}</strong>
                {row.moqDelta > 0 && (
                  <span className="mt-1 block font-semibold text-amber-800">
                    → {dcUnits(row.distributionQty)}
                    <span className="block text-[10px] font-normal">MOQ</span>
                  </span>
                )}
                <span className="mt-1 block text-[10px] text-muted-foreground">
                  units
                </span>
              </td>
              {centers.map(dc => {
                const cell = row.allocations[dc.id]
                return (
                  <td key={dc.id}>
                    <DcPercentCell
                      value={cell.percent}
                      label={`${row.line.sku} ${row.line.type} ${dc.code} allocation percent`}
                      onChange={value => onEdit(row, dc.id, value)}
                    />
                    <div
                      className="dc-quantity"
                      title={`Case pack size: ${dcUnits(row.logistics.multiple)} units. Theoretical: ${dcUnits(cell.raw)} units → rounded up to full case packs: ${dcUnits(cell.adjusted)} units. Difference: ${dcDelta(cell.delta)} units.`}
                    >
                      <span>{dcUnits(cell.raw)}</span>
                      <span aria-hidden="true">→</span>
                      <strong>{dcUnits(cell.adjusted)}</strong>
                    </div>
                  </td>
                )
              })}
              <td className="dc-total-column">
                <span
                  className={`dc-status ${row.balanced ? 'dc-status-ok' : 'dc-status-review'}`}
                >
                  {row.balanced && <Check size={11} />}
                  {dcUnits(row.percent)}% split
                </span>
                {!row.balanced && (
                  <span className="mt-1 block text-[11px] text-destructive">
                    {dcUnits(Math.abs(100 - row.percent))}%{' '}
                    {row.percent < 100 ? 'remaining' : 'over target'}
                  </span>
                )}
                <strong className="mt-1 block">
                  {dcUnits(row.adjusted)}{' '}
                  <span className="text-[11px] font-normal text-muted-foreground">
                    adjusted
                  </span>
                </strong>
                <span
                  className={`dc-balance ${row.balance > 1e-7 ? 'text-amber-800' : row.balance < -1e-7 ? 'text-destructive' : 'text-muted-foreground'}`}
                >
                  {row.balance > 1e-7
                    ? 'Excess '
                    : row.balance < -1e-7
                      ? 'Short '
                      : ''}
                  {dcDelta(row.balance)} units
                  {Math.abs(row.balance) > 1e-7 && row.waveQty > 0 && (
                    <> ({dcDelta((row.balance / row.waveQty) * 100)}%)</>
                  )}
                </span>
              </td>
            </tr>
          ))}
          {!rows.length && (
            <tr>
              <td
                colSpan={centers.length + 3}
                className="!py-14 text-center text-sm text-muted-foreground"
              >
                No SKU lines match these filters.
              </td>
            </tr>
          )}
        </tbody>
        <tfoot>
          <tr>
            <th scope="row" className="dc-sku-column">
              Filtered totals
              <span>{totalRows.length} lines · excludes Parent units</span>
            </th>
            <td>{dcUnits(totals.waveQty)}</td>
            {totals.centers.map(dc => (
              <td key={dc.id}>
                <span className="font-normal text-muted-foreground">
                  {dcUnits(dc.raw)}
                </span>
                <strong className="mt-1 block">{dcUnits(dc.adjusted)}</strong>
              </td>
            ))}
            <td className="dc-total-column">
              <strong>{dcUnits(totals.adjusted)}</strong>
              <span>{dcDelta(totals.balance)} vs. wave</span>
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  )
}
